import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { HeroSectionServices } from "../services/HeroSectionServices";

/* ─── Inline styles ─────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Outfit:wght@300;400;500;600&display=swap');

  .hero-root {
    font-family: 'Outfit', sans-serif;
    padding: 32px 0;
  }

  .hero-track {
    border-radius: 24px;
    overflow: hidden;
    position: relative;
    min-height: 520px;
    transition: background 1s ease;
  }

  /* Decorative elements */
  .hero-blob {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    filter: blur(80px);
    opacity: 0.28;
    transition: all 1s ease;
  }
  .hero-blob-1 { width: 500px; height: 500px; top: -150px; left: -100px; background: rgba(255,255,255,0.3); }
  .hero-blob-2 { width: 380px; height: 380px; bottom: -100px; right: -80px; background: rgba(255,255,255,0.18); }

  .hero-grain {
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0.04;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
  }

  .hero-lines {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: repeating-linear-gradient(
      90deg,
      rgba(255,255,255,0.04) 0px,
      rgba(255,255,255,0.04) 1px,
      transparent 1px,
      transparent 80px
    );
  }

  /* Slick base fix */
  .hero-root .slick-slider,
  .hero-root .slick-list,
  .hero-root .slick-track {
    height: 100%;
  }
  .hero-root .slick-slide > div {
    height: 100%;
  }

  /* Arrows */
  .hero-root .slick-arrow {
    z-index: 20;
    width: 48px; height: 48px;
    border-radius: 50%;
    border: 1px solid rgba(0,0,0,0.15);
    background: rgba(255,255,255,0.65);
    backdrop-filter: blur(12px);
    display: flex !important;
    align-items: center;
    justify-content: center;
    color: #1a1a2e;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.25s ease;
    box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  }
  .hero-root .slick-arrow:hover {
    background: rgba(255,255,255,0.92);
    border-color: rgba(0,0,0,0.25);
    transform: scale(1.08);
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  }
  .hero-root .slick-prev { left: 22px; }
  .hero-root .slick-next { right: 22px; }

  /* Dots */
  .hero-root .slick-dots { bottom: 22px; }
  .hero-root .slick-dots li button:before {
    color: rgba(0,0,0,0.3);
    font-size: 7px;
    transition: all 0.25s;
  }
  .hero-root .slick-dots li.slick-active button:before {
    color: #0f1923;
    font-size: 9px;
  }

  /* Slide inner */
  .hero-slide {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    padding: 64px 72px;
    min-height: 520px;
    position: relative;
    z-index: 2;
  }

  /* Static (no-slider) layout for single slide */
  .hero-static-slide {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    padding: 64px 72px;
    min-height: 520px;
    position: relative;
    z-index: 2;
  }

  /* Left content */
  .hero-content { flex: 1; max-width: 540px; }

  .hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #1a1a2e;
    background: rgba(255,255,255,0.72);
    border: 1px solid rgba(0,0,0,0.1);
    border-radius: 100px;
    padding: 6px 16px;
    margin-bottom: 24px;
    backdrop-filter: blur(8px);
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  }
  .hero-eyebrow::before {
    content: "";
    display: block;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #0d6efd;
    flex-shrink: 0;
  }

  .hero-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(36px, 4.5vw, 60px);
    font-weight: 900;
    line-height: 1.1;
    color: #0f1923;
    margin: 0 0 20px;
    text-shadow:
      0 1px 0 rgba(255,255,255,0.6),
      0 4px 24px rgba(0,0,0,0.12);
    letter-spacing: -0.5px;
  }

  .hero-subtitle {
    font-size: 16px;
    font-weight: 400;
    line-height: 1.7;
    color: #2d3748;
    margin: 0 0 40px;
    max-width: 420px;
    text-shadow: 0 1px 4px rgba(255,255,255,0.5);
  }

  /* CTA group */
  .hero-cta-group {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  .hero-cta-primary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 14px 30px;
    border-radius: 100px;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-decoration: none;
    background: #0f1923;
    color: #fff;
    border: none;
    cursor: pointer;
    transition: all 0.25s ease;
    box-shadow: 0 6px 28px rgba(0,0,0,0.22);
  }
  .hero-cta-primary:hover {
    background: #1e3a5f;
    transform: translateY(-2px);
    box-shadow: 0 12px 36px rgba(0,0,0,0.3);
    color: #fff;
  }
  .hero-cta-primary .cta-icon { font-size: 17px; transition: transform 0.22s; }
  .hero-cta-primary:hover .cta-icon { transform: translateX(4px); }

  /* Right image */
  .hero-image-wrap {
    flex: 0 0 auto;
    width: 440px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
  .hero-image-ring {
    position: absolute;
    width: 360px; height: 360px;
    border-radius: 50%;
    border: 1px solid rgba(0,0,0,0.08);
    pointer-events: none;
  }
  .hero-image-ring-2 {
    position: absolute;
    width: 280px; height: 280px;
    border-radius: 50%;
    border: 1px solid rgba(0,0,0,0.05);
    pointer-events: none;
  }
  .hero-image-glow {
    position: absolute;
    width: 300px; height: 300px;
    border-radius: 50%;
    background: rgba(255,255,255,0.45);
    filter: blur(55px);
    pointer-events: none;
  }
  .hero-image-wrap img {
    position: relative;
    z-index: 1;
    max-width: 400px;
    max-height: 440px;
    width: 100%;
    object-fit: contain;
    filter: drop-shadow(0 24px 48px rgba(0,0,0,0.2));
    animation: heroFloat 5s ease-in-out infinite;
  }

  /* Scroll btn */
  .hero-scroll-btn {
    position: absolute;
    bottom: 0; left: 50%;
    transform: translateX(-50%);
    z-index: 20;
    width: 48px; height: 48px;
    border-radius: 50% 50% 0 0;
    background: rgba(0,0,0,0.08);
    border: 1px solid rgba(0,0,0,0.12);
    border-bottom: none;
    backdrop-filter: blur(10px);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1a1a2e;
    font-size: 18px;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.2s;
  }
  .hero-scroll-btn:hover { background: rgba(0,0,0,0.15); color: #1a1a2e; }
  .hero-scroll-btn i { animation: scrollBounce 2s ease-in-out infinite; }

  /* Slide entry animations */
  .hero-slide.is-active .hero-eyebrow   { animation: heroFadeUp 0.5s 0.05s both; }
  .hero-slide.is-active .hero-title     { animation: heroFadeUp 0.55s 0.15s both; }
  .hero-slide.is-active .hero-subtitle  { animation: heroFadeUp 0.55s 0.28s both; }
  .hero-slide.is-active .hero-cta-group { animation: heroFadeUp 0.55s 0.4s both; }
  .hero-slide.is-active .hero-image-wrap { animation: heroFadeIn 0.75s 0.2s both; }

  /* Static slide always visible */
  .hero-static-slide .hero-eyebrow   { animation: heroFadeUp 0.5s 0.1s both; }
  .hero-static-slide .hero-title     { animation: heroFadeUp 0.55s 0.2s both; }
  .hero-static-slide .hero-subtitle  { animation: heroFadeUp 0.55s 0.32s both; }
  .hero-static-slide .hero-cta-group { animation: heroFadeUp 0.55s 0.44s both; }
  .hero-static-slide .hero-image-wrap { animation: heroFadeIn 0.75s 0.25s both; }

  @keyframes heroFadeUp  { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; } }
  @keyframes heroFadeIn  { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
  @keyframes heroFloat   { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-16px); } }
  @keyframes scrollBounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(5px); } }

  /* Skeleton */
  .hero-skeleton {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    padding: 64px 72px;
    min-height: 520px;
  }
  .skel {
    background: rgba(255,255,255,0.1);
    border-radius: 12px;
    animation: skelShimmer 1.5s ease-in-out infinite;
  }
  .skel-t1  { height: 60px; width: 70%; margin-bottom: 18px; }
  .skel-t2  { height: 14px; width: 55%; margin-bottom: 10px; }
  .skel-t3  { height: 14px; width: 40%; margin-bottom: 32px; }
  .skel-btn { height: 48px; width: 150px; border-radius: 100px; }
  .skel-img { width: 300px; height: 300px; border-radius: 50%; flex-shrink: 0; }
  @keyframes skelShimmer { 0%,100% { opacity: 0.35; } 50% { opacity: 0.65; } }

  /* Responsive */
  @media (max-width: 991px) {
    .hero-slide, .hero-static-slide {
      flex-direction: column;
      padding: 52px 36px 72px;
      text-align: center;
      min-height: 440px;
    }
    .hero-content { max-width: 100%; }
    .hero-subtitle { max-width: 100%; margin-left: auto; margin-right: auto; }
    .hero-cta-group { justify-content: center; }
    .hero-image-wrap { width: 100%; max-width: 260px; margin: 0 auto; }
    .hero-image-wrap img { max-height: 220px; animation: none; }
    .hero-image-ring, .hero-image-ring-2 { display: none; }
    .hero-skeleton { flex-direction: column; padding: 48px 32px; }
    .skel-img { width: 200px; height: 200px; }
  }
  @media (max-width: 575px) {
    .hero-track { border-radius: 16px; }
    .hero-slide, .hero-static-slide { padding: 40px 24px 68px; }
    .hero-title { font-size: 30px; }
  }
`;

const FALLBACK_GRADIENT = "linear-gradient(135deg, #1a3a5c 0%, #2d7d6f 100%)";

function buildBackground(slide) {
  const raw = slide?.backgroundColor;
  if (!raw) return FALLBACK_GRADIENT;
  if (/^#/.test(raw) || /^rgb/.test(raw)) {
    return `linear-gradient(135deg, ${raw} 0%, ${adjustLightness(raw, -24)} 100%)`;
  }
  return raw;
}

function adjustLightness(hex, amount) {
  const n = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, Math.max(0, (n >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((n >> 8) & 0xff) + amount));
  const b = Math.min(255, Math.max(0, (n & 0xff) + amount));
  return `rgb(${r},${g},${b})`;
}

const Arrow = ({ className, onClick, icon }) => (
  <button type="button" onClick={onClick} className={className} aria-label="Slide navigation">
    <i className={`ph ${icon}`} />
  </button>
);

const Skeleton = () => (
  <div className="hero-skeleton">
    <div style={{ flex: 1 }}>
      <div className="skel skel-t1" />
      <div className="skel skel-t2" />
      <div className="skel skel-t3" />
      <div className="skel skel-btn" />
    </div>
    <div className="skel skel-img" />
  </div>
);

const SlideContent = ({ slide, isActive, isStatic }) => {
  const className = isStatic ? "hero-static-slide" : `hero-slide${isActive ? " is-active" : ""}`;

  return (
    <div className={className}>
      <div className="hero-content">
        {slide.backgroundName && (
          <span className="hero-eyebrow">{slide.backgroundName}</span>
        )}
        <h1 className="hero-title">{slide.title}</h1>
        {slide.subtitle && (
          <p className="hero-subtitle">{slide.subtitle}</p>
        )}
        <div className="hero-cta-group">
          <Link to="/shop" className="hero-cta-primary">
            Shop Now
            <i className="ph ph-arrow-right cta-icon" />
          </Link>
        </div>
      </div>

      {slide.image && (
        <div className="hero-image-wrap">
          <div className="hero-image-ring" />
          <div className="hero-image-ring-2" />
          <div className="hero-image-glow" />
          <img src={slide.image} alt={slide.title} loading="eager" />
        </div>
      )}
    </div>
  );
};

const BannerOne = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);

  const settings = useMemo(
    () => ({
      dots: true,
      arrows: true,
      infinite: true,
      speed: 1100,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 5500,
      pauseOnHover: true,
      nextArrow: <Arrow icon="ph-caret-right" />,
      prevArrow: <Arrow icon="ph-caret-left" />,
      beforeChange: (_, next) => setCurrentSlide(next),
    }),
    []
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await HeroSectionServices.list();
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];

        const normalized = list
          .map((item) => ({
            id: item.id,
            title: item.title || "",
            subtitle: item.subtitle || "",
            image: item.image || "",
            backgroundColor:
              item?.background_image?.color_code ||
              item?.backgroundImage?.color_code ||
              "#1a3a5c",
            backgroundName:
              item?.background_image?.name ||
              item?.backgroundImage?.name ||
              "",
          }))
          .filter((x) => x.title || x.image);

        if (alive) { setSlides(normalized); setCurrentSlide(0); }
      } catch (err) {
        console.error("Hero load failed:", err);
        if (alive) setSlides([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const activeBg = buildBackground(slides[currentSlide]);
  const isSingle = slides.length === 1;

  return (
    <>
      <style>{css}</style>
      <div className="hero-root">
        <div className="container container-lg">
          <div
            className="hero-track"
            style={{ background: activeBg }}
          >
            <div className="hero-blob hero-blob-1" />
            <div className="hero-blob hero-blob-2" />
            <div className="hero-grain" />
            <div className="hero-lines" />

            {loading ? (
              <Skeleton />
            ) : slides.length === 0 ? (
              <div
                className="hero-static-slide"
                style={{ justifyContent: "center", flexDirection: "column", textAlign: "center" }}
              >
                <h2 className="hero-title" style={{ fontSize: "32px" }}>No hero content yet</h2>
                <p className="hero-subtitle" style={{ margin: "0 auto" }}>
                  Add hero items from your admin panel and they will appear here.
                </p>
              </div>
            ) : isSingle ? (
              /* ── FIX: single slide — render directly, no Slider wrapper ── */
              <SlideContent slide={slides[0]} isStatic={true} />
            ) : (
              <Slider ref={sliderRef} {...settings}>
                {slides.map((slide, idx) => (
                  <SlideContent
                    key={slide.id}
                    slide={slide}
                    isActive={idx === currentSlide}
                    isStatic={false}
                  />
                ))}
              </Slider>
            )}

            <a href="#featureSection" className="hero-scroll-btn" aria-label="Scroll down">
              <i className="ph ph-caret-double-down" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default BannerOne;